'use client'

import { hasUnsavedAdminWork, requestAdminNavigation } from '@/lib/admin-navigation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  clearAdminSecret,
  getAdminExpiry,
  getAdminSecret,
  isAdminAuthStorageKey,
  setAdminSecret,
  subscribeAdminAccessRefused,
  subscribeAdminAuth,
  verifyAdminSecret,
} from '@/lib/admin-auth'
import { useQueryClient } from '@tanstack/react-query'
import { Eye, EyeOff, Lock } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

export function AdminAccessGate({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [adminEmail, setAdminEmail] = useState('')
  const [inputSecret, setInputSecret] = useState('')
  const [showSecret, setShowSecret] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [authRevision, setAuthRevision] = useState(0)
  const verifiedLogin = useRef<{ secret: string, email: string } | null>(null)
  const authenticatedSecret = useRef<string | null>(null)
  const keptSecret = useRef<string | null>(null)
  const askOnLoss = useRef(false)

  const resetSession = useCallback(() => {
    authenticatedSecret.current = null
    askOnLoss.current = false
    queryClient.clear()
    setIsAuthenticated(null)
    setAdminEmail('')
    setAuthRevision(revision => revision + 1)
  }, [queryClient])

  const signOut = useCallback((message: string) => {
    if (!requestAdminNavigation())
      return false
    verifiedLogin.current = null
    clearAdminSecret()
    if (authenticatedSecret.current !== null)
      resetSession()
    setInputSecret('')
    setError(message)
    return true
  }, [resetSession])

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (!isAdminAuthStorageKey(event.key))
        return
      verifiedLogin.current = null
      if (authenticatedSecret.current !== null && hasUnsavedAdminWork())
        setAuthRevision(revision => revision + 1)
      else
        resetSession()
    }
    const onFocus = () => {
      if (getAdminSecret())
        setAuthRevision(revision => revision + 1)
      else if (authenticatedSecret.current !== null && !hasUnsavedAdminWork())
        resetSession()
    }
    const onAccessRefused = (secret: string, method: string) => {
      const byAdmin = method === 'POST'
      if (hasUnsavedAdminWork() && (!byAdmin || keptSecret.current === secret))
        return
      if (byAdmin)
        askOnLoss.current = true
      setAuthRevision(revision => revision + 1)
    }

    const unsubscribe = subscribeAdminAuth(resetSession)
    const unsubscribeRefusals = subscribeAdminAccessRefused(onAccessRefused)
    window.addEventListener('storage', onStorage)
    window.addEventListener('focus', onFocus)

    return () => {
      unsubscribe()
      unsubscribeRefusals()
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('focus', onFocus)
    }
  }, [resetSession])

  useEffect(() => {
    let cancelled = false
    const stored = getAdminSecret()
    if (!stored) {
      if (authenticatedSecret.current === null) {
        setIsAuthenticated(false)
        setAdminEmail('')
      }
      return
    }

    const expires = getAdminExpiry()
    const expiryTimer = expires === null
      ? undefined
      : window.setTimeout(() => { getAdminSecret() }, Math.max(0, expires - Date.now() + 1))

    if (verifiedLogin.current?.secret === stored) {
      keptSecret.current = null
      askOnLoss.current = false
      authenticatedSecret.current = stored
      setAdminEmail(verifiedLogin.current.email)
      setIsAuthenticated(true)
      verifiedLogin.current = null
      return () => {
        if (expiryTimer !== undefined)
          window.clearTimeout(expiryTimer)
      }
    }

    const wasAuthenticated = authenticatedSecret.current !== null
    if (!wasAuthenticated)
      setIsAuthenticated(null)
    verifyAdminSecret(stored).then((result) => {
      if (cancelled)
        return
      const asked = askOnLoss.current
      askOnLoss.current = false
      if (getAdminSecret() !== stored)
        return
      if (result.ok) {
        keptSecret.current = null
        authenticatedSecret.current = stored
        setAdminEmail(result.email)
        setIsAuthenticated(true)
      }
      else if (!wasAuthenticated) {
        setError(result.error)
        if (result.rejected)
          clearAdminSecret()
        setIsAuthenticated(false)
      }
      else if (result.rejected
        && (!hasUnsavedAdminWork() || (asked && keptSecret.current !== stored))
        && !signOut(result.error)) {
        keptSecret.current = stored
      }
    })

    return () => {
      cancelled = true
      if (expiryTimer !== undefined)
        window.clearTimeout(expiryTimer)
    }
  }, [authRevision, signOut])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    const secret = inputSecret.trim()
    const result = await verifyAdminSecret(secret)
    if (result.ok) {
      verifiedLogin.current = { secret, email: result.email }
      setAdminSecret(secret)
      setInputSecret('')
    }
    else {
      setError(result.error)
    }

    setIsLoading(false)
  }

  const handleLogout = () => {
    signOut('')
  }

  if (isAuthenticated === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="size-6 animate-spin rounded-full border-2 border-current border-t-transparent" />
      </div>
    )
  }

  if (isAuthenticated) {
    return (
      <div className="relative">
        { }
        <div className="fixed right-4 top-4 z-50">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="bg-background/80 backdrop-blur-sm"
            title={adminEmail}
          >
            <Lock className="mr-2 size-4" />
            Logout
          </Button>
        </div>
        {children}
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10">
            <Lock className="size-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Admin Access</CardTitle>
          <p className="text-muted-foreground">
            Enter your Mentiss API secret (sk_mentiss_...). Your account must
            be a verified @mentiss.ai admin.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="api-secret">API Secret</Label>
              <div className="relative">
                <Input
                  id="api-secret"
                  type={showSecret ? 'text' : 'password'}
                  value={inputSecret}
                  onChange={e => setInputSecret(e.target.value)}
                  placeholder="sk_mentiss_..."
                  className="pr-10"
                  autoComplete="off"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowSecret(!showSecret)}
                >
                  {showSecret ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </Button>
              </div>
            </div>

            {error && (
              <div className="text-center text-sm text-red-500">{error}</div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !inputSecret.trim()}
            >
              {isLoading ? (
                <>
                  <div className="mr-2 size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Verifying...
                </>
              ) : (
                <>
                  <Lock className="mr-2 size-4" />
                  Access Admin Panel
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
